import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { errMsg, postWithRetry, authErrorMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
//
// Retry policy for the /auth/google/session exchange:
//   - The endpoint proxies to Emergent's OAuth service, which occasionally
//     returns a 502/503/timeout mid-handshake, causing the "click Google
//     -> bounce back to /login" symptom.
//   - We retry TRANSIENT failures (network error, 5xx) up to 3 attempts
//     with backoff via postWithRetry; permanent failures (400/401/403 from
//     the OAuth service — e.g. session already consumed / expired) do
//     NOT retry.
//   - After retries exhaust, we show a visible error + Retry button
//     rather than a silent bounce. We keep the hash intact until the
//     token is committed so Retry can reuse the same session_id.
const MAX_ATTEMPTS = 3;
const BACKOFF_MS = [400, 1200];

async function exchangeGoogleSession(sessionId) {
  const { data } = await postWithRetry(
    "/auth/google/session",
    { session_id: sessionId },
    { attempts: MAX_ATTEMPTS, backoffMs: BACKOFF_MS },
  );
  return data;
}

export default function AuthCallback() {
  const inFlight = useRef(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("processing"); // processing | error
  const [errorMsg, setErrorMsg] = useState("");
  const [attemptTick, setAttemptTick] = useState(0);

  useEffect(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus("processing");

    const params = new URLSearchParams(window.location.hash.slice(1));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      // no session in URL — nothing to exchange
      navigate("/login", { replace: true });
      return;
    }

    exchangeGoogleSession(sessionId)
      .then((data) => {
        // 1. Persist the token FIRST — synchronously — so any subsequent
        //    request (from Dashboard mount etc.) has it available.
        if (data?.token) {
          localStorage.setItem("eg_token", data.token);
          // 2. Belt & braces: confirm the write is readable before proceeding.
          if (localStorage.getItem("eg_token") !== data.token) {
            throw new Error("We couldn't save your sign-in on this device — please try again.");
          }
        }
        // 3. Commit user in context — this makes ProtectedRoute pass without
        //    a /me round-trip (login() does not call /me).
        login(data);
        // 4. Only NOW strip the hash — we no longer need the session_id.
        window.history.replaceState(null, "", window.location.pathname);
        // 5. Navigate.
        navigate("/dashboard", { replace: true });
      })
      .catch((e) => {
        // Do NOT strip the hash — Retry needs the same session_id.
        // Do NOT bounce to /login silently — show a visible error.
        setStatus("error");
        const status = e.response?.status;
        let msg;
        if (status === 400 || status === 401 || status === 403) {
          msg = errMsg(e) || "Your Google sign-in link has expired. Please try signing in again.";
        } else {
          msg = authErrorMsg(e, "Google sign-in didn't complete. Please try again.");
        }
        setErrorMsg(msg);
        inFlight.current = false; // allow explicit Retry
      });
  }, [navigate, login, attemptTick]);

  if (status === "error") {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="auth-callback-error">
        <div className="max-w-md w-full rounded-3xl border border-border bg-card p-8 text-center">
          <p className="text-sm font-semibold text-destructive mb-2">Google sign-in didn&apos;t complete</p>
          <p className="text-xs text-muted-foreground mb-6" data-testid="auth-callback-error-msg">{errorMsg}</p>
          <div className="flex gap-2 justify-center">
            <Button data-testid="auth-callback-retry-btn" onClick={() => setAttemptTick((t) => t + 1)} size="sm" className="rounded-full glow-btn">
              Retry
            </Button>
            <Button variant="outline" data-testid="auth-callback-back-to-login-btn" size="sm" className="rounded-full"
              onClick={() => { window.history.replaceState(null, "", window.location.pathname); navigate("/login", { replace: true }); }}>
              Back to login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-xs text-muted-foreground" data-testid="auth-callback-status">Signing you in with Google…</p>
      </div>
    </div>
  );
}
